import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import api from '../lib/api';
import { CalendarHeart, ChevronLeft, ChevronRight, MapPin, Globe } from 'lucide-react';
import { toast } from 'sonner';

const formatDate = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: 'long' });
};

const isPast = (dateStr) => {
  const today = new Date().toISOString().split('T')[0];
  return dateStr < today;
};

const isToday = (dateStr) => {
  return dateStr === new Date().toISOString().split('T')[0];
};

export default function Feiertage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadHolidays = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/feiertage/${year}`);
      setData(res.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Feiertage');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  const years = [];
  for (let y = new Date().getFullYear(); y <= new Date().getFullYear() + 3; y++) {
    years.push(y);
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-[500px] rounded-xl" />
        </div>
      </DashboardLayout>
    );
  }

  const groups = data?.groups || [];

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="feiertage-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-heading font-bold text-3xl text-foreground">Feiertage</h1>
            <p className="text-muted-foreground mt-1">
              Gesetzliche Feiertage nach Bundesland der Niederlassung
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <Button variant="outline" size="icon" onClick={() => setYear(y => y - 1)} data-testid="year-prev">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="flex-1 sm:flex-none sm:w-[120px]" data-testid="year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={() => setYear(y => y + 1)} data-testid="year-next">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {groups.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <CalendarHeart className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="text-lg font-medium mb-1">Kein Bundesland konfiguriert</p>
              <p className="text-sm">Bitte weisen Sie Ihren Niederlassungen ein Bundesland zu unter Organisation &gt; Niederlassungen.</p>
            </CardContent>
          </Card>
        ) : (
          groups.map((group) => (
            <Card key={group.bundesland} className="border-border/60" data-testid={`holiday-group-${group.bundesland}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-primary" />
                      {group.bundesland_name}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Niederlassungen: {group.branch_names.join(', ')} &middot; {group.holidays.length} Feiertage
                    </CardDescription>
                  </div>
                  <Badge variant="secondary" className="text-base px-3 py-1">
                    {group.holidays.length}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[200px]">Datum</TableHead>
                        <TableHead>Feiertag</TableHead>
                        <TableHead className="text-right">Geltungsbereich</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.holidays.map((h) => (
                        <TableRow
                          key={h.date}
                          className={`${isPast(h.date) ? 'opacity-50' : ''} ${isToday(h.date) ? 'bg-primary/5 font-medium' : ''}`}
                        >
                          <TableCell className="font-mono">
                            <div className="flex items-center gap-2">
                              {isToday(h.date) && <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />}
                              {formatDate(h.date)}
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{h.name}</TableCell>
                          <TableCell className="text-right">
                            {h.nationwide ? (
                              <Badge variant="default" className="gap-1">
                                <Globe className="h-3 w-3" />
                                Bundesweit
                              </Badge>
                            ) : (
                              <Badge variant="outline">Landesspezifisch</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </DashboardLayout>
  );
}
